import {Cell} from '../cell';
import {CellArea} from '../docking/cell-area';
import {CellAreaType} from '../docking/cell-area-type';
import {Layout} from '../layout';
import {LayoutChanges} from '../layout-changes';
import {LayoutFactory} from '../layout.factory';
import {Widget} from '../widget/widget';
import {CellActionType} from './cell-action-type';

export class WidgetActions {

    private layoutFactory: LayoutFactory = this.layout.injector.get(LayoutFactory);

    constructor(
        public layout: Layout
    ) {}

    public moveWidget(widget: Widget, targetArea: CellArea): LayoutChanges {
        const changes: LayoutChanges = new LayoutChanges();

        switch (targetArea.type) {
            case CellAreaType.HEADER:
            case CellAreaType.CENTER:
                changes.append(this.moveWidgetToExistingCell(widget, targetArea.cell));
                break;

            case CellAreaType.EMPTY:
            case CellAreaType.EDGE:
            case CellAreaType.SIDE:
                changes.append(this.moveWidgetToArea(widget, targetArea));
                break;
        }

        return changes;
    }

    public excludeWidgetFromLayout(widget: Widget): LayoutChanges {
        const changes: LayoutChanges = new LayoutChanges();
        const cell: Cell = widget.cell;

        if (cell) {
            const widgetIndexInCell = cell.widgets.indexOf(widget);

            if (widgetIndexInCell >= 0) {
                cell.widgets.splice(widgetIndexInCell, 1);
            }

            if (cell.activeWidget === widget) {
                cell.activeWidget = cell.widgets[0] || null;
            }

            widget.cell = null;

            changes.add(
                'Widget excluded from Cell',
                {cell, widget},
                {actionType: CellActionType.WIDGET_DELETE}
            );
        }

        return changes;
    }

    public moveWidgetToExistingCell(widget: Widget, targetCell?: Cell): LayoutChanges {
        const changes: LayoutChanges = new LayoutChanges();
        const oldCell: Cell = widget.cell;

        changes
            .append(this.excludeWidgetFromLayout(widget))
            .append(this.appendWidgetToCell(widget, targetCell));

        if (oldCell && !oldCell.widgets.length) {
            changes.append(this.layout.cellActions.removeCell(oldCell));
        }

        if (targetCell) {
            changes.append(this.layout.setCellActiveWidget(targetCell, widget));
        }

        return changes;
    }

    public removeWidget(widget: Widget): LayoutChanges {
        const changes: LayoutChanges = new LayoutChanges();
        const cell: Cell = widget.cell;

        if (cell) {
            changes.append(this.excludeWidgetFromLayout(widget));

            if (!cell.widgets.length) {
                changes.append(this.layout.cellActions.removeCell(cell));
            }
        }

        return changes;
    }

    public moveWidgetToAnotherLayout(
        widget: Widget,
        anotherLayout: Layout
    ): [LayoutChanges, LayoutChanges] {
        const thisLayoutChanges: LayoutChanges = new LayoutChanges();
        const anotherLayoutChanges = new LayoutChanges();

        widget.setTabId(anotherLayout.tabId);

        this.layout.widgetActions.moveWidgetToExistingCell(widget, null)
            .appendTo(thisLayoutChanges);

        const newCell: Cell = this.layoutFactory.createCellByDefinition({
            id: null,
            activeWidgetIndex: null,
            widgets: []
        });

        anotherLayoutChanges
            .append(anotherLayout.widgetActions.moveWidgetToExistingCell(widget, newCell))
            .add(
                'Widget placed from other Layout',
                {cell: newCell},
                {actionType: CellActionType.CELL_NEW}
            )
            .append(anotherLayout.cellActions.insertCell(newCell));

        return [thisLayoutChanges, anotherLayoutChanges];
    }

    private appendWidgetToCell(widget: Widget, cell?: Cell): LayoutChanges {
        const changes: LayoutChanges = new LayoutChanges();

        this.setWidgetCell(widget, cell);

        if (cell) {
            changes.add(
                'Widget added to Cell',
                {cell, widget},
                {actionType: CellActionType.WIDGET_ADD}
            );
        }

        return changes;
    }

    private setWidgetCell(widget: Widget, cell?: Cell): void {
        widget.cell = cell;

        if (cell) {
            if (!cell.widgets.includes(widget)) {
                cell.widgets.push(widget);
            }

            if (!cell.activeWidget) {
                cell.activeWidget = widget;
            }
        }
    }

    private moveWidgetToArea(widget: Widget, targetArea: CellArea): LayoutChanges {
        const changes: LayoutChanges = new LayoutChanges();
        const cell: Cell = widget.cell;

        if (!cell || cell.widgets.length > 1) {
            const newCell: Cell = this.layoutFactory.createCellByDefinition({
                id: null,
                activeWidgetIndex: null,
                widgets: []
            });

            changes
                .append(this.excludeWidgetFromLayout(widget))
                .add('New Cell created', {cell: newCell}, {actionType: CellActionType.CELL_NEW})
                .append(this.appendWidgetToCell(widget, newCell))
                .append(this.layout.cellActions.insertCell(newCell, targetArea));
        } else {
            changes.append(this.layout.cellActions.moveCell(cell, targetArea));
        }

        return changes;
    }

}
